/**
 * Job 삭제 확인 모달.
 *
 * D3 = simple_confirm — 간단한 확인 모달.
 *
 * 디자인 포인트:
 *   - 위험 액션이라 빨간색 (red-600) primary 버튼.
 *   - 배치명 / 파일 수 / 상태를 한 줄로 표시해 잘못 선택했는지 즉시 식별 가능.
 *   - Esc / backdrop 클릭으로 닫기 (ResetPasswordModal 과 동일한 UX).
 *   - 진행 중에는 양쪽 버튼 모두 disabled (중복 클릭 방지).
 */

import { useEffect } from "react";
import { AlertCircle, AlertTriangle, Loader2, X } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import type { JobOut } from "@/lib/api/jobs";

import { useDeleteJob } from "./useDeleteJob";

interface Props {
  job: JobOut;
  onClose: () => void;
}

export default function DeleteJobConfirmModal({ job, onClose }: Props) {
  const { t } = useTranslation();
  const mutation = useDeleteJob();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !mutation.isPending) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, mutation.isPending]);

  const apiError =
    mutation.error instanceof ApiError ? mutation.error.detail : (mutation.error?.message ?? null);

  const handleConfirm = () => {
    if (mutation.isPending) return;
    mutation.mutate(job.id, { onSuccess: () => onClose() });
  };

  const handleBackdrop = () => {
    if (mutation.isPending) return;
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-8"
      onClick={handleBackdrop}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="del-job-title"
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 id="del-job-title" className="text-sm font-semibold text-slate-900">
            {t("jobs.delete.title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
            aria-label={t("common.actions.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="space-y-4 px-5 py-4">
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            {/*
             * <Trans> 사용 — ``jobs.delete.danger`` 키는 "...<strong>X件...
             * </strong>..." 패턴이라 inline 마크업이 필요. ``values`` 로 count
             * 를 주입, ``components`` 의 ``strong`` 매핑이 강조 ``<span>``.
             */}
            <div>
              <Trans
                i18nKey="jobs.delete.danger"
                values={{ count: job.file_count }}
                components={{ strong: <span className="font-semibold" /> }}
              />
            </div>
          </div>

          <dl className="space-y-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs">
            <div className="flex">
              <dt className="w-20 flex-shrink-0 text-slate-500">
                {t("jobs.delete.row.batchName")}
              </dt>
              <dd className="min-w-0 flex-1 truncate font-medium text-slate-900">
                {job.batch_name || (
                  <span className="text-slate-400">{t("common.placeholders.anonymous")}</span>
                )}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-20 flex-shrink-0 text-slate-500">{t("jobs.delete.row.jobId")}</dt>
              <dd className="min-w-0 flex-1 truncate font-mono text-[11px] text-slate-700">
                {job.id}
              </dd>
            </div>
            <div className="flex">
              <dt className="w-20 flex-shrink-0 text-slate-500">
                {t("jobs.delete.row.statusAndFiles")}
              </dt>
              <dd className="min-w-0 flex-1 text-slate-700">
                {t("jobs.delete.row.statusFilesValue", {
                  status: job.status,
                  count: job.file_count,
                })}
              </dd>
            </div>
          </dl>

          {apiError ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{apiError}</span>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : null}
            {t("common.actions.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}
